import { useState } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Trash2, GripVertical, Plus, Minus } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import type { Question, QuestionOption } from '@/types/questionnaire';
import { v4 as uuidv4 } from 'uuid';

interface QuestionEditorProps {
  question: Question;
  onUpdate: (updates: Partial<Question>) => void;
  onDelete: () => void;
}

export function QuestionEditor({ question, onUpdate, onDelete }: QuestionEditorProps) {
  const { t, dir } = useLanguage();

  const updateQuestionText = (language: 'en' | 'ar', text: string) => {
    onUpdate({
      question: {
        ...question.question,
        [language]: text
      }
    });
  };

  const updateOptionText = (optionId: string, language: 'en' | 'ar', text: string) => {
    const updatedOptions = question.options.map(option =>
      option.id === optionId
        ? {
            ...option,
            text: {
              ...option.text,
              [language]: text
            }
          }
        : option
    );
    onUpdate({ options: updatedOptions });
  };

  const updateOptionScore = (optionId: string, score: number) => {
    const updatedOptions = question.options.map(option =>
      option.id === optionId
        ? { ...option, score }
        : option
    );
    onUpdate({ options: updatedOptions });
  };

  const addOption = () => {
    if (question.options.length >= 6) return; // Max 6 options
    
    const newOption: QuestionOption = {
      id: uuidv4(),
      text: { en: '', ar: '' },
      score: 0
    };
    
    onUpdate({ options: [...question.options, newOption] });
  };

  const removeOption = (optionId: string) => {
    if (question.options.length <= 2) return; // Min 2 options
    
    const updatedOptions = question.options.filter(option => option.id !== optionId);
    onUpdate({ options: updatedOptions });
  };

  return (
    <Card className="relative bg-gray-50">
      <CardHeader className="pb-3">
        <div className="flex items-start gap-3">
          <GripVertical className="h-5 w-5 text-muted-foreground mt-1 cursor-move" />
          <div className="flex-1 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label htmlFor={`question-en-${question.id}`} className="text-sm font-medium">
                  {t('dashboard.admin.questionnaires.questionEn')}
                </Label>
                <Input
                  id={`question-en-${question.id}`}
                  value={question.question.en}
                  onChange={(e) => updateQuestionText('en', e.target.value)}
                  placeholder="Enter question in English"
                  className="mt-1 bg-white"
                />
              </div>
              <div>
                <Label htmlFor={`question-ar-${question.id}`} className="text-sm font-medium">
                  {t('dashboard.admin.questionnaires.questionAr')}
                </Label>
                <Input
                  id={`question-ar-${question.id}`}
                  value={question.question.ar}
                  onChange={(e) => updateQuestionText('ar', e.target.value)}
                  placeholder="أدخل السؤال بالعربية"
                  className="mt-1 bg-white"
                  dir="rtl"
                />
              </div>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onDelete}
            className="text-destructive hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium">
            {t('dashboard.admin.questionnaires.options')} ({question.options.length}/6)
          </Label>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={addOption}
              disabled={question.options.length >= 6}
              className="h-8"
            >
              <Plus className="h-3 w-3 mr-1" />
              {t('dashboard.admin.questionnaires.addOption')}
            </Button>
          </div>
        </div>
        
        {question.options.map((option, index) => (
          <div key={option.id} className="grid grid-cols-1 md:grid-cols-6 gap-3 p-3 border rounded-lg">
            <div className="md:col-span-2">
              <Label htmlFor={`option-en-${option.id}`} className="text-xs text-muted-foreground">
                English
              </Label>
              <Input
                id={`option-en-${option.id}`}
                value={option.text.en}
                onChange={(e) => updateOptionText(option.id, 'en', e.target.value)}
                placeholder={`Option ${index + 1} (EN)`}
                className="mt-1 bg-white"
              />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor={`option-ar-${option.id}`} className="text-xs text-muted-foreground">
                العربية
              </Label>
              <Input
                id={`option-ar-${option.id}`}
                value={option.text.ar}
                onChange={(e) => updateOptionText(option.id, 'ar', e.target.value)}
                placeholder={`الخيار ${index + 1}`}
                className="mt-1 bg-white"
                dir="rtl"
              />
            </div>
            <div>
              <Label htmlFor={`score-${option.id}`} className="text-xs text-muted-foreground">
                {t('dashboard.admin.questionnaires.score')}
              </Label>
              <Input
                id={`score-${option.id}`}
                type="number"
                min="0"
                max="10"
                value={option.score}
                onChange={(e) => updateOptionScore(option.id, parseInt(e.target.value) || 0)}
                className="mt-1 bg-white"
              />
            </div>
            <div className="flex items-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeOption(option.id)}
                disabled={question.options.length <= 2}
                className="text-destructive hover:text-destructive h-8"
              >
                <Minus className="h-3 w-3" />
              </Button>
            </div>
          </div>
        ))}
        
        {question.options.length < 2 && (
          <div className="text-sm text-muted-foreground text-center py-2">
            {t('dashboard.admin.questionnaires.minOptionsRequired')}
          </div>
        )}
        {question.options.length >= 6 && (
          <div className="text-sm text-muted-foreground text-center py-2">
            {t('dashboard.admin.questionnaires.maxOptionsReached')}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
